import React, {useEffect, useState} from "react";
import FormButton from "@app/component/form/button/FormButton";
import {ChevronRightIcon} from "@heroicons/react/20/solid";
import useToaster from "@app/component/overlays/toaster/ToasterHook";
import CompleteRegistrationInput from "@app/model/registration/input/CompleteRegistrationInput";
import {useTranslation} from "@app/component/translation/Translation";
import Principal from "@app/model/authentication/Principal";
import usePropertiesState from "@app/site/registration/fragment/state/PropertiesState";
import AddPropertiesGoogleMapFragment from "@app/site/registration/flow/AddPropertiesGoogleMapFragment";
import SetupPropertyFormFragment from "@app/site/registration/fragment/SetupPropertyFormFragment";
import SetupPropertyDisplayFragment from "@app/site/registration/fragment/SetupPropertyDisplayFragment";
import Property from "@app/site/registration/fragment/state/Property";
import {useMutation} from "@tanstack/react-query";
import RegistrationService from "@app/model/registration/service/RegistrationService";

const AddPropertiesFlowPage: React.FunctionComponent<{
    onComplete: (principal: Principal) => any,
    webhotelierId?: string | null,
}> = ({onComplete, webhotelierId}) => {
    const state = usePropertiesState()
    const toaster = useToaster();
    const {t} = useTranslation();
    const [editIndex, setEditIndex] = useState<number | undefined>();
    const [handledProperty, setHandledProperty] = useState<Property>();

    useEffect(() => {
        setEditIndex(state.properties.length === 1 ? 0 : undefined);
        if (!!webhotelierId) {
            state.setProperty(0, {hotel: undefined, webhotelierId: webhotelierId, competitors: [undefined]})
        }
    }, []);

    const resetIndex = (properties: Property[]) => {
        setEditIndex(properties.length === 1 && !properties[0].hotel ? 0 : undefined);
    }

    const onRemove = (index: number) => {
        resetIndex(state.removeProperty(index));
    }

    const onAddNew = () => {
        setEditIndex(state.addNewProperty());
    }

    const createCompanyHotelPropertiesMutation = useMutation({
        mutationFn: RegistrationService.createCompanyHotelProperties,
        onSuccess: onComplete,
        onError: (error: string) => {
            toaster.error("Registration", t(error))
        }
    })

    const onFinish = () => {
        const validationError = state.isValid();
        if (!validationError) {
            const input: CompleteRegistrationInput = {
                properties: state.properties.map(p => {
                    return {
                        hotelId: p.hotel!.id, webhotelierId: p.webhotelierId, competitorIds: p.competitors.map(c => c!.id)
                    }
                })
            }
            createCompanyHotelPropertiesMutation.mutate(input)
        } else toaster.error("Registration", validationError)
    }

    return <div className="min-h-full bg-white">
        <div className="flex lg:h-dvh min-w-max content-center justify-center flex-1">

            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
                    <div>
                        <img
                            className="h-10 w-auto"
                            src="/assets/img/brand/logo-icon.png"
                            alt="RoomPulse"
                        />
                        <div className="flex flex-wrap items-center justify-between sm:flex-nowrap mt-8">
                            <div>
                                <h2 className="text-2xl font-bold leading-9 tracking-tight text-gray-900">
                                    {!editIndex && editIndex != 0 ? 'Your Properties' : 'Set Up Your Property'}
                                </h2>
                            </div>
                            <div className="sm:ml-4 flex-shrink-0 space-x-2">
                                {editIndex === undefined && <FormButton state="primary" onClick={onFinish}>
                                    <span>Finish</span>
                                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true"/>
                                </FormButton>}
                            </div>
                        </div>
                        <ul role="list"
                            className="mt-4 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
                            {state.properties.map((property, idx) => editIndex === idx ?
                                <SetupPropertyFormFragment key={`input_${idx}`} onComplete={resetIndex} index={idx}
                                                           onChange={setHandledProperty} />
                                : <SetupPropertyDisplayFragment index={idx} onEdit={setEditIndex} onRemove={onRemove}
                                                                key={`input_${idx}`}/>)}
                        </ul>
                        {!editIndex && editIndex != 0 &&
                            <div className="flex justify-end border-t border-gray-200 pt-4">
                                <FormButton state="text" isLink onClick={onAddNew}>
                                    <span aria-hidden="true" className="mr-1">+</span>Add property
                                </FormButton>
                            </div>}
                    </div>
                </div>

            </div>
            <div className="relative hidden flex-1 lg:block">
                <AddPropertiesGoogleMapFragment property={handledProperty}/>
            </div>
        </div>
    </div>
}
export default AddPropertiesFlowPage;